/**
 * Learning Center API: the catalog editor (plan docs/plans/lms-plan.md 8.1,
 * 6.5, 6.6, Appendix A "Courses and catalog"; 18.11, 18.17; WP-30's routes,
 * WP-44's client; R8, R10, R11). Typed calls over services/api (the shared
 * axios instance that adds the JWT and X-Site-Id); nothing under src/lms
 * imports axios (2.3a). Every route below is an lms admin route
 * (requireLmsAdmin) except the course list, which answers the admin view only
 * to an lms admin (?all=1, 18.17).
 *
 *   GET  /courses?all=1                        -> { items (with origin, draft_version), page }
 *   POST /admin/courses                        -> 201 { ...course, draft_version: { id, version_no } | null }
 *   PATCH /admin/courses/:id                   -> course
 *   GET  /admin/courses/:id/versions           -> { items: version summaries, newest first, page }
 *   POST /admin/courses/:id/versions {from}    -> 201 the draft's detail (the id is at .version.id, 18.17)
 *   POST /admin/courses/:id/retire {reason}    -> course
 *   GET  /admin/versions/:id                   -> { version, course, content, questions, quiz_settings, sources, lint, impact }
 *   PUT  /admin/versions/:id                   -> the same (the autosave; the lint comes back and never blocks a save)
 *   POST /admin/versions/:id/lint              -> { ok, errors }
 *   POST /admin/versions/:id/publish           -> { version, campaign, superseded_completions }
 *                                                 (400 LMS_CONTENT_INVALID with { lint } on lint errors)
 *   POST /admin/versions/:id/discard {reason?} -> the discarded version summary
 *   GET  /admin/versions/:id/diff/:otherId     -> { version, other, sections }
 *   GET  /admin/catalog/status                 -> { pending, files }
 *   POST /admin/catalog/install                -> { installed, skipped, blocked, profiles }
 *
 * Query strings travel as axios `params`, so the URL itself stays the path
 * (the test harness routes GETs by exact URL).
 */
import api from '../../services/api';
import type {
  CourseCategory,
  CourseContent,
  CourseKind,
  CourseQuestion,
  CourseSource,
  VersionQuizSettings,
} from '../content/types';
import type { LmsList } from './client';

const enc = encodeURIComponent;

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

/** 5.2 lms_course_versions.retrain_policy (8.1, R11). */
export type RetrainPolicy = 'immediate' | 'acknowledge' | 'at_renewal';

/** 5.2 lms_course_versions.status. */
export type VersionStatus = 'draft' | 'published' | 'superseded' | 'retired';

/** 5.2 lms_courses.origin. */
export type CourseOrigin = 'builtin' | 'custom';

/** One GET /courses?all=1 item: the learner fields plus the admin view's (reads.js listCourses). */
export interface AdminCourseItem {
  id: string;
  slug: string;
  title: string;
  category: CourseCategory;
  kind: CourseKind;
  validity_months: number | null;
  estimated_minutes: number | null;
  current_version: { id: string; version_no: number; published_at: string | null } | null;
  is_active: boolean;
  origin?: CourseOrigin;
  builtin_slug?: string | null;
  renewal_lead_days?: number;
  description?: string | null;
  /** The course's open draft (5.2: at most one), or null. */
  draft_version?: { id: string; version_no: number } | null;
}

/** A version row without its bulky jsonb (versions.js versionSummary). */
export interface VersionSummary {
  id: string;
  course_id: string;
  version_no: number;
  status: VersionStatus;
  content_sha256: string | null;
  change_summary: string | null;
  estimated_minutes: number | null;
  retrain_policy: RetrainPolicy | null;
  retrain_due_days: number | null;
  published_at: string | null;
  published_by: string | null;
  superseded_at: string | null;
  retired_at: string | null;
  created_by: string | null;
  created_at: string | null;
}

/** The course on a version detail (versions.js courseSummary). */
export interface VersionCourse {
  id: string;
  slug: string;
  title: string;
  category: CourseCategory;
  kind: CourseKind;
  origin: CourseOrigin;
  builtin_slug: string | null;
  validity_months: number | null;
  renewal_lead_days: number;
  is_active: boolean;
  current_version_id: string | null;
}

/** One content lint problem (18.11): schema problems carry rule 'schema' and a code. */
export interface LintError {
  rule: string;
  path: string;
  message: string;
  code?: string;
}

export interface LintResult {
  ok: boolean;
  errors: LintError[];
}

/** What publishing a draft touches (8.1's dialog numbers); null unless the version is a draft. */
export interface PublishImpact {
  valid_completions: number;
  open_assignments: number;
}

/** A quiz question as the editor reads it: with its correct flags (8.1) and its row id. */
export interface EditorQuestion extends CourseQuestion {
  id?: string;
}

/** GET /admin/versions/:id (and the PUT and draft-create answers). */
export interface VersionDetail {
  version: VersionSummary;
  course: VersionCourse;
  content: CourseContent;
  questions: EditorQuestion[];
  quiz_settings: VersionQuizSettings;
  sources: CourseSource[];
  lint: LintResult;
  impact: PublishImpact | null;
}

/** PUT /admin/versions/:id: only the fields that changed (unknown fields are refused, 400). */
export interface DraftPatch {
  content?: CourseContent;
  questions?: CourseQuestion[];
  quiz_settings?: VersionQuizSettings;
  sources?: CourseSource[];
  change_summary?: string | null;
  estimated_minutes?: number;
}

/** POST /admin/versions/:id/publish (8.1). Version 1 is coerced to at_renewal (18.17). */
export interface PublishBody {
  retrain_policy: RetrainPolicy;
  retrain_due_days?: number;
  change_summary?: string | null;
}

export interface PublishResult {
  version: VersionSummary;
  campaign: { id: string; recipient_count: number | null } | null;
  superseded_completions: number;
}

export type DiffStatus = 'added' | 'removed' | 'changed' | 'same';

export interface DiffSlide {
  id: string;
  status: DiffStatus;
  changed_fields: string[];
}

export interface DiffReadingLine {
  op: 'same' | 'added' | 'removed';
  text: string;
}

export interface DiffSection {
  id: string;
  title: string | null;
  status: DiffStatus;
  reading_changed: boolean;
  reading_diff: DiffReadingLine[];
  slides: DiffSlide[];
}

/** GET /admin/versions/:id/diff/:otherId: what changed in `version` compared with `other` (18.17). */
export interface VersionDiff {
  version: VersionSummary;
  other: VersionSummary;
  sections: DiffSection[];
}

/** POST /admin/courses (8.1 "New course"). */
export interface NewCourseBody {
  title: string;
  slug?: string;
  category: CourseCategory;
  kind: CourseKind;
  validity_months?: number | null;
  renewal_lead_days?: number;
  description?: string | null;
}

/** The POST /admin/courses answer: the course row plus its first draft. */
export interface CreatedCourse {
  id: string;
  slug: string;
  title: string;
  category: CourseCategory;
  kind: CourseKind;
  draft_version: { id: string; version_no: number } | null;
  [extra: string]: unknown;
}

/** PATCH /admin/courses/:id (8.1: the slug is editable until the first publish). */
export interface CoursePatch {
  title?: string;
  slug?: string;
  description?: string | null;
  validity_months?: number | null;
  renewal_lead_days?: number;
  is_active?: boolean;
}

/** One catalog file's state (18.17). */
export type CatalogFileState = 'not_installed' | 'up_to_date' | 'changed' | 'draft_open' | 'slug_taken' | 'course_inactive';

export interface CatalogFileStatus {
  slug: string;
  file: string;
  latest_version_no: number | null;
  status: CatalogFileState;
  draft_version_no: number | null;
}

export interface CatalogStatus {
  pending: number;
  files: CatalogFileStatus[];
}

export interface CatalogInstallResult {
  installed: Array<{ slug: string; version_no: number; action?: 'created' | 'updated' }>;
  skipped: string[];
  blocked?: Array<{ slug: string; reason: string; draft_version_no: number | null }>;
  profiles: {
    created: number;
    skipped: number;
    requirements_added?: number;
    missing_courses?: Array<{ profile: string; slug: string }>;
  };
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/** Appendix A caps a page at 200; the admin list is walked page by page, never assumed to fit. */
const PAGE_LIMIT = 200;
/** A guard against a server that keeps saying hasMore (G10: fail loudly, never loop forever). */
const MAX_PAGES = 50;

/** GET /api/lms/courses?all=1: every course of the network with its draft (8.1, 18.17). */
export async function listAdminCourses(): Promise<AdminCourseItem[]> {
  const out: AdminCourseItem[] = [];
  for (let page = 0; page < MAX_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const response = await api.get<LmsList<AdminCourseItem>>('/api/lms/courses', {
      params: { all: 1, limit: PAGE_LIMIT, offset: page * PAGE_LIMIT },
    });
    const items = Array.isArray(response.data?.items) ? response.data.items : [];
    out.push(...items);
    if (!response.data?.page?.hasMore) return out;
  }
  throw new Error('The course list did not end after 50 pages.');
}

/** POST /api/lms/admin/courses */
export async function createCourse(body: NewCourseBody): Promise<CreatedCourse> {
  const response = await api.post<CreatedCourse>('/api/lms/admin/courses', body);
  return response.data;
}

/** PATCH /api/lms/admin/courses/:id */
export async function updateCourse(courseId: string, patch: CoursePatch): Promise<unknown> {
  const response = await api.patch(`/api/lms/admin/courses/${enc(courseId)}`, patch);
  return response.data;
}

/** GET /api/lms/admin/courses/:id/versions (newest first). */
export async function listCourseVersions(courseId: string): Promise<VersionSummary[]> {
  const response = await api.get<LmsList<VersionSummary>>(`/api/lms/admin/courses/${enc(courseId)}/versions`, {
    params: { limit: PAGE_LIMIT },
  });
  return Array.isArray(response.data?.items) ? response.data.items : [];
}

/** POST /api/lms/admin/courses/:id/versions {from}: the new draft's detail (409 when a draft is open). */
export async function createDraft(courseId: string, from: 'current' | 'empty'): Promise<VersionDetail> {
  const response = await api.post<VersionDetail>(`/api/lms/admin/courses/${enc(courseId)}/versions`, { from });
  return response.data;
}

/** POST /api/lms/admin/courses/:id/retire {reason} */
export async function retireCourse(courseId: string, reason: string): Promise<unknown> {
  const response = await api.post(`/api/lms/admin/courses/${enc(courseId)}/retire`, { reason });
  return response.data;
}

/** GET /api/lms/admin/versions/:id */
export async function getVersion(versionId: string): Promise<VersionDetail> {
  const response = await api.get<VersionDetail>(`/api/lms/admin/versions/${enc(versionId)}`);
  return response.data;
}

/** PUT /api/lms/admin/versions/:id: the draft autosave (8.1). */
export async function saveDraft(versionId: string, patch: DraftPatch): Promise<VersionDetail> {
  const response = await api.put<VersionDetail>(`/api/lms/admin/versions/${enc(versionId)}`, patch);
  return response.data;
}

/** POST /api/lms/admin/versions/:id/lint */
export async function lintVersion(versionId: string): Promise<LintResult> {
  const response = await api.post<LintResult>(`/api/lms/admin/versions/${enc(versionId)}/lint`, {});
  return response.data;
}

/** POST /api/lms/admin/versions/:id/publish */
export async function publishVersion(versionId: string, body: PublishBody): Promise<PublishResult> {
  const response = await api.post<PublishResult>(`/api/lms/admin/versions/${enc(versionId)}/publish`, body);
  return response.data;
}

/** POST /api/lms/admin/versions/:id/discard {reason?} */
export async function discardDraft(versionId: string, reason?: string | null): Promise<VersionSummary> {
  const body = reason && reason.trim() ? { reason: reason.trim() } : {};
  const response = await api.post<VersionSummary>(`/api/lms/admin/versions/${enc(versionId)}/discard`, body);
  return response.data;
}

/** GET /api/lms/admin/versions/:id/diff/:otherId: what changed in :id compared with :otherId. */
export async function diffVersions(versionId: string, otherId: string): Promise<VersionDiff> {
  const response = await api.get<VersionDiff>(`/api/lms/admin/versions/${enc(versionId)}/diff/${enc(otherId)}`);
  return response.data;
}

/** GET /api/lms/admin/catalog/status */
export async function getCatalogStatus(): Promise<CatalogStatus> {
  const response = await api.get<CatalogStatus>('/api/lms/admin/catalog/status');
  return response.data;
}

/** POST /api/lms/admin/catalog/install (the body is empty: the route refuses any field). */
export async function installCatalog(): Promise<CatalogInstallResult> {
  const response = await api.post<CatalogInstallResult>('/api/lms/admin/catalog/install', {});
  return response.data;
}
